import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  AlertTriangle, 
  Coins, 
  Zap, 
  ArrowRight,
  X,
  Sparkles,
  ZoomIn,
  Wand2,
  Layers
} from 'lucide-react';
import BuyCreditsModal from './BuyCreditsModal';
import UpgradeModal from './UpgradeModal';
import { 
  trackLowCreditWarning, 
  trackNudgeShown, 
  trackNudgeClicked, 
  trackButtonClick,
  trackSmartNudgeShown,
  trackSmartNudgeDismissed,
  trackLoopStep,
  LOOP_STEPS
} from '@/services/analytics';

// CORRECT Credit costs matching backend (Phase 4)
const CREDIT_COSTS = {
  image_generate: 5,       // Generate → 5 credits
  background_generate: 3,
  mockup_generate: 5,
  make_it_better: 3,       // Make It Better → 3 credits
  enhance: 1,              // Enhance → 1 credit
  upscale_2x: 4,           // Upscale 2x → 4 credits
  upscale_4x: 6,           // Upscale 4x → 6 credits
  refine: 3,
  variations: 3
};

const LOW_CREDIT_THRESHOLD = 10;
const CRITICAL_CREDIT_THRESHOLD = 5;

const LowCreditWarning = ({ 
  onUpgrade, 
  dismissible = true,
  variant = 'banner',  // 'banner', 'inline', 'modal'
  showWhen = 'low'  // 'low', 'critical', 'always'
}) => {
  const { user, token } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [hasTracked, setHasTracked] = useState(false);
  
  const credits = user?.credits || 0;
  const isAdmin = user?.is_admin; // Admin testing mode - bypass credit warnings
  const isCritical = credits <= CRITICAL_CREDIT_THRESHOLD;
  const isLow = credits <= LOW_CREDIT_THRESHOLD;
  
  // Track warning display (only once per session)
  useEffect(() => {
    if (!hasTracked && (isLow || isCritical) && !dismissed) {
      const trigger = isCritical ? 'critical' : 'warning';
      trackLowCreditWarning(credits, trigger, token);
      setHasTracked(true);
    }
  }, [credits, isCritical, isLow, hasTracked, dismissed, token]);
  
  // Determine if we should show the warning
  const shouldShow = () => {
    if (dismissed) return false;
    if (isAdmin) return false; // Never show for admins in testing mode
    if (showWhen === 'always') return true;
    if (showWhen === 'critical') return isCritical;
    return isLow;
  };
  
  // Calculate estimated actions remaining
  const getEstimatedActions = () => {
    if (credits <= 0) return { images: 0, backgrounds: 0, mockups: 0 };
    return {
      images: Math.floor(credits / CREDIT_COSTS.image_generate),
      backgrounds: Math.floor(credits / CREDIT_COSTS.background_generate),
      mockups: Math.floor(credits / CREDIT_COSTS.mockup_generate),
      improvements: Math.floor(credits / CREDIT_COSTS.make_it_better)
    };
  };
  
  const handleBuyCreditsClick = () => {
    trackButtonClick('buy_credits_warning', 'low_credit_banner', token);
    setShowBuyCredits(true);
  };
  
  const handleUpgradeClick = () => {
    trackButtonClick('upgrade_plan_warning', 'low_credit_banner', token);
    if (onUpgrade) onUpgrade();
  };
  
  const actions = getEstimatedActions();
  
  if (!shouldShow()) return null;
  
  if (variant === 'inline') {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
            isCritical 
              ? 'bg-red-500/10 text-red-600 border border-red-500/20' 
              : 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
          }`}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">
            {isCritical 
              ? `Only ${credits} credits left!` 
              : `${credits} credits remaining`
            }
          </span>
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-7 px-2 text-xs"
            onClick={handleBuyCreditsClick}
          >
            <Coins className="w-3 h-3 mr-1" />
            Buy
          </Button>
        </motion.div>
        
        <BuyCreditsModal 
          isOpen={showBuyCredits} 
          onClose={() => setShowBuyCredits(false)} 
        />
      </>
    );
  }
  
  // Banner variant (default)
  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className={`relative overflow-hidden rounded-xl border ${
            isCritical 
              ? 'bg-gradient-to-r from-red-500/10 to-orange-500/10 border-red-500/20' 
              : 'bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-amber-500/20'
          }`}
        >
          <div className="p-4">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                isCritical ? 'bg-red-500/20' : 'bg-amber-500/20'
              }`}>
                <AlertTriangle className={`w-5 h-5 ${isCritical ? 'text-red-500' : 'text-amber-500'}`} />
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <h4 className={`font-semibold ${isCritical ? 'text-red-600' : 'text-amber-600'}`}>
                  {isCritical ? "You're almost out of credits!" : "You're running low on credits"}
                </h4>
                
                <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Coins className="w-4 h-4" />
                    <span className="font-semibold">{credits}</span> credits remaining
                  </div>
                  <div className="text-xs">
                    ≈ {actions.images} images • {actions.improvements} improvements
                  </div>
                </div>
                
                {/* Action buttons */}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button 
                    size="sm" 
                    className={isCritical ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'}
                    onClick={handleBuyCreditsClick}
                    data-testid="low-credit-buy-btn"
                  >
                    <Coins className="w-4 h-4 mr-2" />
                    Buy Credits
                  </Button>
                  {onUpgrade && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={handleUpgradeClick}
                      data-testid="low-credit-upgrade-btn"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Upgrade Plan
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Dismiss button */}
              {dismissible && (
                <button 
                  onClick={() => setDismissed(true)}
                  className="p-1 hover:bg-background/50 rounded transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
      
      <BuyCreditsModal 
        isOpen={showBuyCredits} 
        onClose={() => setShowBuyCredits(false)} 
      />
    </>
  );
};

// Credit Cost Display component - shows cost before action
export const CreditCostDisplay = ({ 
  action, 
  customCost = null,
  showBalance = true,
  showRemaining = true,
  className = ""
}) => {
  const { user } = useAuth();
  const credits = user?.credits || 0;
  const isAdmin = user?.is_admin; // Admin testing mode
  const cost = customCost || CREDIT_COSTS[action] || 0;
  const remaining = credits - cost;
  const canAfford = isAdmin || remaining >= 0; // Admins can always afford
  
  return (
    <div className={`flex items-center gap-3 text-sm ${className}`}>
      <div className="flex items-center gap-1.5">
        <Coins className="w-4 h-4 text-muted-foreground" />
        <span className={canAfford ? 'text-foreground' : 'text-red-500'}>
          {cost} credits
        </span>
      </div>
      {showBalance && (
        <div className="text-muted-foreground">
          Balance: {credits}{isAdmin && ' (Admin)'}
        </div>
      )}
      {showRemaining && (
        <div className={`text-xs ${canAfford ? 'text-green-600' : 'text-red-500'}`}>
          {isAdmin ? '→ Admin Mode' : (canAfford ? `→ ${remaining} remaining` : 'Insufficient credits')}
        </div>
      )}
    </div>
  );
};

// Post-generation nudge component with CORRECT costs and tracking
export const PostGenerationNudge = ({ 
  onMakeItBetter, 
  onEnhance, 
  onUpscale,
  isEnhanced = false,
  isUpscaled = false,
  makingBetter = false
}) => {
  const { user, token } = useAuth();
  const credits = user?.credits || 0;
  const isAdmin = user?.is_admin; // Admin testing mode - bypass credit checks
  const [nudgesTracked, setNudgesTracked] = useState(false);
  
  // Track nudge display once
  useEffect(() => {
    if (!nudgesTracked) {
      trackNudgeShown('make_it_better', token);
      if (!isEnhanced) trackNudgeShown('enhance', token);
      if (!isUpscaled) trackNudgeShown('upscale', token);
      setNudgesTracked(true);
    }
  }, [nudgesTracked, isEnhanced, isUpscaled, token]);
  
  const handleMakeItBetterClick = () => {
    trackNudgeClicked('make_it_better', token);
    if (onMakeItBetter) onMakeItBetter();
  };
  
  const handleEnhanceClick = () => {
    trackNudgeClicked('enhance', token);
    if (onEnhance) onEnhance();
  };
  
  const handleUpscaleClick = (scale) => {
    trackNudgeClicked('upscale', token);
    if (onUpscale) onUpscale(scale);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="mt-4 p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl border border-primary/20"
    >
      <p className="text-sm font-medium text-foreground mb-3">
        Want even better results?
      </p>
      
      <div className="flex flex-wrap gap-2">
        {/* Make It Better - 3 credits (CORRECT) */}
        {onMakeItBetter && (
          <Button 
            size="sm"
            onClick={handleMakeItBetterClick}
            disabled={makingBetter || (!isAdmin && credits < 3)}
            className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
            data-testid="nudge-make-better-btn"
          >
            {makingBetter ? (
              <>Processing...</>
            ) : (
              <>
                <Zap className="w-4 h-4 mr-1" />
                Make It Better
                <span className="ml-1 text-xs opacity-80">(3 cr)</span>
              </>
            )}
          </Button>
        )}
        
        {/* Enhance - 1 credit (CORRECT) */}
        {onEnhance && !isEnhanced && (
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleEnhanceClick}
            disabled={!isAdmin && credits < 1}
            data-testid="nudge-enhance-btn"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            Enhance
            <span className="ml-1 text-xs text-muted-foreground">(1 cr)</span>
          </Button>
        )}
        
        {/* Upscale 2x - 4 credits (CORRECT) */}
        {onUpscale && !isUpscaled && (
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => handleUpscaleClick(2)}
            disabled={!isAdmin && credits < 4}
            data-testid="nudge-upscale-btn"
          >
            <ZoomIn className="w-4 h-4 mr-1" />
            Upscale 2x
            <span className="ml-1 text-xs text-muted-foreground">(4 cr)</span>
          </Button>
        )}
      </div>
      
      {!isAdmin && credits < 3 && (
        <p className="mt-2 text-xs text-amber-600">
          Low credits? Buy more to keep creating!
        </p>
      )}
    </motion.div>
  );
};

// Conversion-Optimized Post-Generation Actions
// Designed to maximize loop completion and credit usage
export const ConversionOptimizedActions = ({
  onMakeItBetter,
  onEnhance,
  onUpscale,
  onDownload,
  onCreateMockup,
  actionsCompleted = 0, // 0 = just generated, 1 = one action done, 2+ = multiple actions
  isImproved = false,
  isEnhanced = false,
  isUpscaled = false,
  isProcessing = false,
  className = ""
}) => {
  const { user, token } = useAuth();
  const credits = user?.credits || 0;
  const isAdmin = user?.is_admin; // Admin testing mode - bypass credit checks
  
  // Track component display
  useEffect(() => {
    trackNudgeShown('conversion_actions', token);
  }, [token]);
  
  // Determine which message to show based on progress
  const getProgressMessage = () => {
    if (isUpscaled) {
      return {
        icon: "🎉",
        text: "Ready to download or create mockup",
        subtext: "Your image is perfected!"
      };
    }
    if (actionsCompleted >= 2 || (isImproved && isEnhanced)) {
      return {
        icon: "👉",
        text: "One more step → make it perfect",
        subtext: "Upscale for print-ready quality"
      };
    }
    if (actionsCompleted === 1 || isImproved || isEnhanced) {
      return {
        icon: "✨",
        text: "Looking better! Keep going",
        subtext: "Most users complete all improvements"
      };
    }
    return {
      icon: "💡",
      text: "Make it sharper, cleaner, more professional",
      subtext: "Most users improve before downloading"
    };
  };
  
  const progress = getProgressMessage();
  
  const handleAction = (action, callback) => {
    trackNudgeClicked(action, token);
    if (callback) callback();
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.4 }}
      className={`p-4 rounded-xl bg-gradient-to-br from-background via-background to-primary/5 border border-border/50 ${className}`}
      data-testid="conversion-actions"
    >
      {/* Progress Message */}
      <div className="flex items-start gap-2 mb-4">
        <span className="text-lg">{progress.icon}</span>
        <div>
          <p className="text-sm font-medium text-foreground">{progress.text}</p>
          <p className="text-xs text-muted-foreground">{progress.subtext}</p>
        </div>
      </div>
      
      {/* Action Buttons */}
      {!isUpscaled ? (
        <div className="flex flex-wrap gap-2">
          {/* Make It Better - PRIMARY ACTION (highlighted) */}
          {onMakeItBetter && !isImproved && (
            <Button
              onClick={() => handleAction('make_it_better', onMakeItBetter)}
              disabled={isProcessing || (!isAdmin && credits < 3)}
              className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-lg shadow-purple-500/25 transition-all hover:shadow-purple-500/40"
              data-testid="action-make-better"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Make It Better
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-white/20 rounded">3 cr</span>
            </Button>
          )}
          
          {/* Already improved - show "Improve More" */}
          {onMakeItBetter && isImproved && (
            <Button
              variant="outline"
              onClick={() => handleAction('make_it_better', onMakeItBetter)}
              disabled={isProcessing || (!isAdmin && credits < 3)}
              className="border-violet-500/30 hover:border-violet-500/50 hover:bg-violet-500/5"
              data-testid="action-improve-more"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Improve More
              <span className="ml-2 text-xs text-muted-foreground">(3 cr)</span>
            </Button>
          )}
          
          {/* Enhance */}
          {onEnhance && !isEnhanced && (
            <Button
              variant="outline"
              onClick={() => handleAction('enhance', onEnhance)}
              disabled={isProcessing || (!isAdmin && credits < 1)}
              data-testid="action-enhance"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Enhance
              <span className="ml-2 text-xs text-muted-foreground">(1 cr)</span>
            </Button>
          )}
          
          {/* Upscale */}
          {onUpscale && !isUpscaled && (
            <Button
              variant={actionsCompleted >= 2 ? "default" : "outline"}
              onClick={() => handleAction('upscale', () => onUpscale(2))}
              disabled={isProcessing || (!isAdmin && credits < 4)}
              className={actionsCompleted >= 2 ? "bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700" : ""}
              data-testid="action-upscale"
            >
              <ZoomIn className="w-4 h-4 mr-2" />
              Upscale 2x
              <span className="ml-2 text-xs opacity-75">(4 cr)</span>
            </Button>
          )}
        </div>
      ) : (
        /* After Upscale - Final Actions */
        <div className="flex flex-wrap gap-2">
          {onDownload && (
            <Button
              onClick={() => handleAction('download', onDownload)}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              data-testid="action-download"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Download HD
            </Button>
          )}
          
          {onCreateMockup && (
            <Button
              variant="outline"
              onClick={() => handleAction('create_mockup', onCreateMockup)}
              disabled={!isAdmin && credits < 5}
              data-testid="action-create-mockup"
            >
              <Layers className="w-4 h-4 mr-2" />
              Create Mockup
              <span className="ml-2 text-xs text-muted-foreground">(5 cr)</span>
            </Button>
          )}
        </div>
      )}
      
      {/* Low Credit Warning - hide for admins */}
      {!isAdmin && credits < 3 && !isUpscaled && (
        <p className="mt-3 text-xs text-amber-500 flex items-center gap-1">
          <Coins className="w-3 h-3" />
          Low credits - buy more to complete improvements
        </p>
      )}
      
      {/* Progress Indicator */}
      <div className="mt-4 flex items-center gap-2">
        <div className="flex gap-1">
          <div className={`w-2 h-2 rounded-full transition-colors ${isImproved ? 'bg-violet-500' : 'bg-muted'}`} title="Improved" />
          <div className={`w-2 h-2 rounded-full transition-colors ${isEnhanced ? 'bg-blue-500' : 'bg-muted'}`} title="Enhanced" />
          <div className={`w-2 h-2 rounded-full transition-colors ${isUpscaled ? 'bg-emerald-500' : 'bg-muted'}`} title="Upscaled" />
        </div>
        <span className="text-xs text-muted-foreground">
          {isUpscaled ? 'Complete!' : `${3 - [isImproved, isEnhanced, isUpscaled].filter(Boolean).length} steps left`}
        </span>
      </div>
    </motion.div>
  );
};

// Smart Nudge - appears after delay if user does nothing
export const SmartNudge = ({
  show = false,
  delaySeconds = 0, // Default to 0 since parent (ImageGenerator) handles the delay
  onMakeItBetter,
  onEnhance,
  onUpscale,
  onDismiss
}) => {
  const { user, token } = useAuth();
  const credits = user?.credits || 0;
  const isAdmin = user?.is_admin; // Admin testing mode - bypass credit checks
  const [visible, setVisible] = useState(false);
  const [hasTracked, setHasTracked] = useState(false);
  
  // Show nudge immediately when show prop becomes true (delay handled by parent)
  // Or after delaySeconds if specified (for standalone usage)
  useEffect(() => {
    if (!show) {
      setVisible(false);
      setHasTracked(false);
      return;
    }
    
    // If delaySeconds is 0, show immediately
    if (delaySeconds <= 0) {
      setVisible(true);
      if (!hasTracked) {
        trackSmartNudgeShown('improve_result', 0, token);
        setHasTracked(true);
      }
      return;
    }
    
    // Otherwise use the specified delay
    const timer = setTimeout(() => {
      setVisible(true);
      if (!hasTracked) {
        trackSmartNudgeShown('improve_result', delaySeconds, token);
        setHasTracked(true);
      }
    }, delaySeconds * 1000);
    
    return () => clearTimeout(timer);
  }, [show, delaySeconds, hasTracked, token]);
  
  const handleDismiss = () => {
    trackSmartNudgeDismissed(token);
    setVisible(false);
    if (onDismiss) onDismiss();
  };
  
  const handleAction = (action, callback) => {
    trackNudgeClicked(action, token);
    setVisible(false);
    if (callback) callback();
  };
  
  if (!visible) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        className="fixed bottom-6 right-6 z-50 max-w-sm"
        data-testid="smart-nudge"
      >
        <div className="bg-background/95 backdrop-blur-xl border border-primary/20 rounded-2xl shadow-2xl p-4">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 hover:bg-muted rounded-full transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
          
          <div className="flex items-start gap-3 pr-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <Wand2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-sm">Want to improve this result?</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Quick actions to make it even better
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 mt-3">
            {/* Make It Better - Primary action */}
            {onMakeItBetter && (isAdmin || credits >= 3) && (
              <Button 
                size="sm"
                onClick={() => handleAction('make_it_better', onMakeItBetter)}
                className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 flex-1"
                data-testid="smart-nudge-improve-btn"
              >
                <Wand2 className="w-3.5 h-3.5 mr-1" />
                Improve
                <span className="ml-1 text-xs opacity-75">(3)</span>
              </Button>
            )}
            
            {/* Enhance */}
            {onEnhance && (isAdmin || credits >= 1) && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleAction('enhance', onEnhance)}
                className="flex-1"
                data-testid="smart-nudge-enhance-btn"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1" />
                Enhance
                <span className="ml-1 text-xs opacity-75">(1)</span>
              </Button>
            )}
            
            {/* Upscale */}
            {onUpscale && (isAdmin || credits >= 4) && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleAction('upscale', () => onUpscale(2))}
                data-testid="smart-nudge-upscale-btn"
              >
                <ZoomIn className="w-3.5 h-3.5 mr-1" />
                2x
              </Button>
            )}
          </div>
          
          {!isAdmin && credits < 3 && (
            <p className="mt-2 text-xs text-amber-500 flex items-center gap-1">
              <Coins className="w-3 h-3" />
              Low on credits - buy more to continue
            </p>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Engagement Loop Tracker - tracks the Generate → Improve → Enhance → Upscale flow
export const useEngagementLoop = () => {
  const { token } = useAuth();
  const [loopState, setLoopState] = useState({
    sessionId: null,
    steps: [],
    startTime: null,
    lastStepTime: null,
  });
  
  // Initialize a new loop session
  const startLoop = () => {
    const sessionId = `loop_${Date.now()}`;
    setLoopState({
      sessionId,
      steps: [LOOP_STEPS.GENERATE],
      startTime: Date.now(),
      lastStepTime: Date.now(),
    });
    trackLoopStep(LOOP_STEPS.GENERATE, { session_id: sessionId, step_index: 0 }, token);
  };
  
  // Record a step in the loop
  const recordStep = (step) => {
    setLoopState(prev => {
      if (!prev.sessionId) return prev;
      
      const newSteps = [...prev.steps, step];
      const stepIndex = newSteps.length - 1;
      
      trackLoopStep(step, { 
        session_id: prev.sessionId, 
        step_index: stepIndex,
        time_since_last: Date.now() - prev.lastStepTime,
        total_time: Date.now() - prev.startTime,
      }, token);
      
      // Check if loop is complete (all 4 steps)
      const allSteps = Object.values(LOOP_STEPS);
      const hasAllSteps = allSteps.every(s => newSteps.includes(s));
      if (hasAllSteps) {
        // Track loop completion with a small delay to ensure step tracking completes first
        setTimeout(() => {
          trackLoopStep('loop_complete', {
            session_id: prev.sessionId,
            total_steps: newSteps.length,
            total_time: Date.now() - prev.startTime,
          }, token);
        }, 100);
      }
      
      return {
        ...prev,
        steps: newSteps,
        lastStepTime: Date.now(),
      };
    });
  };
  
  // Reset the loop
  const resetLoop = () => {
    setLoopState({
      sessionId: null,
      steps: [],
      startTime: null,
      lastStepTime: null,
    });
  };
  
  // Get current loop progress
  const getProgress = () => {
    const allSteps = Object.values(LOOP_STEPS);
    return {
      completed: loopState.steps,
      remaining: allSteps.filter(s => !loopState.steps.includes(s)),
      percentage: Math.round((loopState.steps.length / allSteps.length) * 100),
    };
  };
  
  return {
    startLoop,
    recordStep,
    resetLoop,
    getProgress,
    loopState,
    LOOP_STEPS,
  };
};

// Progressive Credit Warning - escalates based on credit level
export const ProgressiveCreditWarning = ({ onUpgrade, onBuyCredits }) => {
  const { user, token } = useAuth();
  const credits = user?.credits || 0;
  const isAdmin = user?.is_admin; // Admin testing mode - never show warnings
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  // At 0 credits, show upgrade modal automatically (but not for admins)
  useEffect(() => {
    if (credits === 0 && !dismissed && !isAdmin) {
      setShowUpgradeModal(true);
    }
  }, [credits, dismissed, isAdmin]);
  
  // Determine warning level
  const getWarningLevel = () => {
    if (isAdmin) return null; // Never show warnings for admins
    if (credits === 0) return 'critical';
    if (credits <= 5) return 'urgent';
    if (credits <= 10) return 'warning';
    return null;
  };
  
  const level = getWarningLevel();
  if (!level || dismissed) return null;
  
  const messages = {
    warning: {
      title: "Running low on credits",
      subtitle: `${credits} credits left`,
      cta: "Get more credits",
    },
    urgent: {
      title: "Almost out of credits!",
      subtitle: `Only ${credits} credits remaining`,
      cta: "Buy credits now",
    },
    critical: {
      title: "You're out of credits",
      subtitle: "Upgrade to continue creating",
      cta: "Upgrade plan",
    },
  };
  
  const config = messages[level];
  const bgColor = level === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                  level === 'urgent' ? 'bg-orange-500/10 border-orange-500/30' :
                  'bg-amber-500/10 border-amber-500/30';
  const textColor = level === 'critical' ? 'text-red-600' :
                    level === 'urgent' ? 'text-orange-600' :
                    'text-amber-600';
  
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex items-center justify-between p-3 rounded-lg border ${bgColor}`}
        data-testid={`credit-warning-${level}`}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className={`w-4 h-4 ${textColor}`} />
          <div>
            <p className={`text-sm font-medium ${textColor}`}>{config.title}</p>
            <p className="text-xs text-muted-foreground">{config.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={level === 'critical' ? 'default' : 'outline'}
            className={level === 'critical' ? 'bg-red-500 hover:bg-red-600' : ''}
            onClick={() => level === 'critical' ? setShowUpgradeModal(true) : (onBuyCredits && onBuyCredits())}
            data-testid="progressive-warning-cta"
          >
            {config.cta}
          </Button>
          {level !== 'critical' && (
            <button
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-background/50 rounded"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>
      </motion.div>
      
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          setDismissed(true);
        }}
        trigger="credits_exhausted"
      />
    </>
  );
};

export default LowCreditWarning;
